import { useAuth0 } from "@auth0/auth0-react";
import { AlertCircle } from "lucide-react";

import { useTranslation } from "@punto/frontend-shared/i18n/i18next";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@punto/ui/components/alert";
import { Button } from "@punto/ui/components/button";

/**
 * Auth0 reports a login rejected for having no organization as prose, under the
 * same generic `invalid_request` code it gives anything else malformed — the
 * description is the only thing that tells the two apart. Matching a vendor
 * string is brittle by nature, so this is a copy upgrade and not a branch the
 * behaviour depends on: a reworded description falls through to the generic
 * message, which is still true.
 */
const NO_ORGANIZATION_HINT = "does not belong to any organization";

/**
 * The terminator of {@link AuthErrorGate}: shown only once the silent recovery
 * has already been spent in this tab. Its button repeats that same recovery —
 * `logout` is what breaks the cycle, because only dropping the Auth0 session
 * makes the next attempt render the universal login form instead of failing
 * against the session that is already there.
 */
export const AuthErrorScreen = ({ error }: { error: Error }) => {
  const { t } = useTranslation();
  const { logout } = useAuth0();

  const description = error.message.includes(NO_ORGANIZATION_HINT)
    ? t("AuthErrorScreen.no_organization")
    : t("AuthErrorScreen.generic");

  return (
    <div className="bg-background fixed inset-0 flex items-center justify-center p-4">
      <div className="flex w-full max-w-md flex-col gap-4">
        <Alert variant="warning">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>{t("AuthErrorScreen.title")}</AlertTitle>
          <AlertDescription>{description}</AlertDescription>
        </Alert>
        <Button
          className="self-center"
          onClick={() =>
            void logout({ logoutParams: { returnTo: window.location.origin } })
          }
        >
          {t("AuthErrorScreen.retry")}
        </Button>
      </div>
    </div>
  );
};
